import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(true);

  // Fetch available currencies from Firebase
  const fetchCurrencies = async () => {
    try {
      setIsLoadingCurrencies(true);
      const currenciesRef = collection(db, 'app', 'currencies', 'available');
      const snapshot = await getDocs(currenciesRef);
      
      const currencies = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        currencies.push({
          id: doc.id,
          name: data.name,
          usdToCoinExchangeRate: data.USDToCoinExchangeRate,
          iso: data.iso,
          amountInSmallestUnit: data.amountInSmallestUnit
        });
      });
      
      // Filter out unwanted currencies
      const excludedCurrencies = ['EUR', 'LBP', 'SYP', 'Winicoins'];
      const filteredCurrencies = currencies.filter(currency => 
        !excludedCurrencies.includes(currency.name)
      );
      
      // Sort currencies alphabetically by name
      filteredCurrencies.sort((a, b) => a.name.localeCompare(b.name));
      setAvailableCurrencies(filteredCurrencies);
      
      // Set default currency to USD if available
      const usdCurrency = filteredCurrencies.find(c => c.name === 'USD');
      if (usdCurrency) {
        setSelectedCurrency('USD');
      } else if (filteredCurrencies.length > 0) {
        setSelectedCurrency(filteredCurrencies[0].name);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      // Fallback to USD if Firebase fails
      setAvailableCurrencies([{ id: 'USD', name: 'USD', usdToCoinExchangeRate: 1, iso: 'US' }]);
      setSelectedCurrency('USD');
    } finally {
      setIsLoadingCurrencies(false);
    }
  };

  // Get selected currency data
  const getSelectedCurrencyData = () => {
    return availableCurrencies.find(c => c.name === selectedCurrency) || 
           { name: 'USD', usdToCoinExchangeRate: 1, iso: 'US' };
  };

  // Convert USD amount to selected currency
  const convertToSelectedCurrency = (usdAmount) => {
    const currencyData = getSelectedCurrencyData();
    return usdAmount * currencyData.usdToCoinExchangeRate;
  };

  // Format amount with currency symbol
  const formatAmount = (amount, currency = selectedCurrency) => {
    const currencyData = availableCurrencies.find(c => c.name === currency) || 
                        { name: 'USD', usdToCoinExchangeRate: 1, iso: 'US' };
    
    const symbol = currencyData.name === 'USD' ? 'US$' : 
                   currencyData.name === 'AED' ? 'AED ' :
                   currencyData.name === 'QAR' ? 'QAR ' :
                   currencyData.name === 'SAR' ? 'SAR ' :
                   currencyData.name;
    
    return `${symbol}${amount.toFixed(2)}`;
  };

  // Format converted amount
  const formatConvertedAmount = (usdAmount) => {
    const convertedAmount = convertToSelectedCurrency(usdAmount);
    return formatAmount(convertedAmount);
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const value = {
    selectedCurrency,
    setSelectedCurrency,
    availableCurrencies,
    isLoadingCurrencies,
    getSelectedCurrencyData,
    convertToSelectedCurrency,
    formatAmount,
    formatConvertedAmount,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
